import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { useI18n } from '../context/I18nContext';
import FileUploadField from './FileUploadField';

/**
 * Dynamic Form Renderer V2.2
 * Supports inline multilingual objects in form.json
 * 
 * onChange(newData, changedFieldId) - 傳遞變更的欄位 ID 讓父組件處理計算
 */
const DynamicFormRenderer = ({ 
    formConfig, 
    formData = {}, 
    onChange, 
    readOnly = false,
    errors = {},
    applicationId = null,
    formId = null,
    onRequireSave = null, // Callback to save form when file upload needs applicationId
}) => {
    const { t, getText } = useI18n(['forms']);

    const [currentStep, setCurrentStep] = useState(0);

    // Determine if layout is stepped (check layout.type, not directly layout)
    const [ isStepped, setIsStepped ] = useState(false);
    useEffect(() => {
        setIsStepped(formConfig?.layout?.type?.value === 'stepped');
    }, [formConfig]);
    const steps = formConfig?.layout?.steps || [];
    const sections = formConfig?.sections || [];
    const fields = formConfig?.fields || {};

    // Filter sections to display based on the current step
    const currentStepId = isStepped && steps[currentStep] ? steps[currentStep].id : null;
    const visibleSections = isStepped 
        ? sections.filter(section => section.step === currentStepId)
        : sections;

    // Handle field value change - 傳遞 changedFieldId 讓父組件處理計算
    const handleFieldChange = (fieldId, value) => {
        const newData = { ...formData, [fieldId]: value };
        // 傳遞 changedFieldId 作為第二個參數
        onChange && onChange(newData, fieldId);
    };

    // Render single field
    const renderField = (fieldId) => {
        const field = fields[fieldId];
        if (!field) return null;

        const value = formData[fieldId] || field.defaultValue || '';
        const isDisabled = readOnly || field.readonly || field.calculated;
        const hasError = errors[fieldId];

        return (
            <div key={fieldId} className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {getText(field.label)}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>

                {renderFieldInput(field, fieldId, value, isDisabled)}

                {field.helperText && (
                    <p className="mt-1 text-xs text-gray-500">{getText(field.helperText)}</p>
                )}
                {hasError && (
                    <p className="mt-1 text-xs text-red-600">{hasError}</p>
                )}
            </div>
        );
    };

    // Render input control
    const renderFieldInput = (field, fieldId, value, isDisabled) => {
        const commonProps = {
            id: fieldId,
            name: fieldId,
            disabled: isDisabled,
            className: `w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                isDisabled ? 'bg-gray-100 cursor-not-allowed' : ''
            } ${errors[fieldId] ? 'border-red-500' : ''}`,
        };

        switch (field.type) {
            case 'text':
                return (
                    <input
                        {...commonProps}
                        type="text"
                        value={value}
                        onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                    />
                );

            case 'number':
                return (
                    <input
                        {...commonProps}
                        type="number"
                        value={value}
                        onChange={(e) => handleFieldChange(fieldId, parseFloat(e.target.value) || 0)}
                    />
                );

            case 'currency':
                return (
                    <div className="relative">
                        <input
                            {...commonProps}
                            type="number"
                            value={value}
                            onChange={(e) => handleFieldChange(fieldId, parseFloat(e.target.value) || 0)}
                        />
                    </div>
                );

            case 'textarea':
                return (
                    <textarea
                        {...commonProps}
                        value={value}
                        rows={field.rows || 3}
                        onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                    />
                );

            case 'date':
                return (
                    <input
                        {...commonProps}
                        type="date"
                        value={value}
                        onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                    />
                );
            case 'time':
                return (
                    <input
                        {...commonProps}
                        type="time"
                        value={value}
                        onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                    />
                );
            case 'datetime':
                return (
                    <input
                        {...commonProps}
                        type="datetime-local"
                        value={value}
                        onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                    />
                );
            case 'select':
                return (
                    <select
                        {...commonProps}
                        value={value}
                        onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                    >
                        <option value="">{t('editor.pleaseSelect', { ns: 'forms' }) || 'Please select'}</option>
                        {field.options?.map(option => (
                            <option key={option.value} value={option.value}>
                                {getText(option.label)}
                            </option>
                        ))}
                    </select>
                );

            case 'radio':
                return (
                    <div className="space-y-2">
                        {field.options?.map(option => (
                            <label key={option.value} className="flex items-center">
                                <input
                                    type="radio"
                                    name={fieldId}
                                    value={option.value}
                                    checked={value === option.value}
                                    disabled={isDisabled}
                                    onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                                    className="mr-2"
                                />
                                <span className="text-sm text-gray-700">{getText(option.label)}</span>
                            </label>
                        ))}
                    </div>
                );

            case 'file':
                // Use FileUploadField for S3 file uploads
                // Note: formId should always be available, but applicationId might be missing in draft
                if (!formId) {
                    return (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                            <p className="text-sm text-yellow-700">
                                File upload requires formId in form renderer
                            </p>
                        </div>
                    );
                }

                return (
                    <FileUploadField
                        fieldId={fieldId}
                        applicationId={applicationId}
                        formId={formId}
                        value={value}
                        onChange={(attachments) => handleFieldChange(fieldId, attachments)}
                        accept={field.accept}
                        maxFiles={field.maxFiles || 5}
                        maxSize={field.maxSize || 50}
                        readOnly={isDisabled}
                        disabled={isDisabled}
                        onRequireSave={onRequireSave}
                    />
                );

            default:
                return (
                    <input
                        {...commonProps}
                        type="text"
                        value={value}
                        onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                    />
                );
        }
    };

    // Render section
    const renderSection = (section) => {
        return (
            <div key={section.id} className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                    {getText(section.title)}
                    {section.optional && <span className="text-sm text-gray-500 ml-2">(Optional)</span>}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.fields.map(fieldId => renderField(fieldId))}
                </div>
            </div>
        );
    };

    // Stepped layout control
    const handleStepChange = (step) => {
        if (step >= 0 && step < steps.length) {
            setCurrentStep(step);
        }
    };

    if (!formConfig || sections.length === 0) {
        return (
            <div className="text-center text-gray-500 py-8">
                Loading form configuration...
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* isStepped Switch, placed at the top right corner of the form title, 4 units from the top */}
            <div className="relative float-right mt-4 mr-4">
                <label className="flex items-center text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={isStepped}
                        onChange={(e) => setIsStepped(e.target.checked)}
                        className="mr-2"
                    />
                    <span>
                        {getText(formConfig?.layout?.type?.label)}
                    </span>
                </label>
            </div>
            {/* Form Title */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <h2 className="text-xl font-bold text-gray-900">{getText(formConfig.formName)}</h2>
                {formConfig.description && (
                    <p className="text-sm text-gray-600 mt-1">{getText(formConfig.description)}</p>
                )}
            </div>

            {/* Stepped Progress Indicator */}
            {isStepped && steps.length > 0 && (
                <div className="flex items-center justify-between mb-6">
                    {steps.map((step, index) => (
                        <Fragment key={step.id}>
                            <div className="flex items-center cursor-pointer" onClick={() => handleStepChange(index)}>
                                <div className={`
                                    w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors
                                    ${index <= currentStep ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}
                                    ${index === currentStep ? 'ring-2 ring-blue-400 ring-offset-2' : ''}
                                `}>
                                    <span>{step.icon || (index + 1)}</span>
                                </div>
                                <span className={`ml-2 text-sm ${index === currentStep ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                                    {getText(step.title)}
                                </span>
                            </div>
                            {index < steps.length - 1 && (
                                <div className={`flex-1 h-1 mx-4 transition-colors ${index < currentStep ? 'bg-blue-600' : 'bg-gray-300'}`} />
                            )}
                        </Fragment>
                    ))}
                </div>
            )}

            {/* Render Sections */}
            {visibleSections.map((section) => renderSection(section))}

            {/* Stepped Navigation Buttons */}
            {isStepped && steps.length > 0 && (
                <div className="flex justify-between mt-6">
                    <button
                        onClick={() => handleStepChange(currentStep - 1)}
                        disabled={currentStep === 0}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {getText(formConfig?.layout?.previous?.icon)} {getText(formConfig?.layout?.previous?.label)}
                    </button>
                    <button
                        onClick={() => handleStepChange(currentStep + 1)}
                        disabled={currentStep === steps.length - 1}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {getText(formConfig?.layout?.next?.label)} {getText(formConfig?.layout?.next?.icon)}
                    </button>
                </div>
            )}
        </div>
    );
};

export default DynamicFormRenderer;
